import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Search, ArrowRightOutlined as ArrowRight } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  OutlinedInput,
  IconButton,
  InputAdornment,
  Button,
  CircularProgress,
  Typography,
  Stack,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Paper,
  Tooltip,
  Autocomplete,
  TextField,
  useTheme,
  Tabs,
  Tab,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ZoomIn as ViewIcon, Download as DownloadIcon, Edit as EditIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  getMachines,
  readAsyncStorageValues,
  getAllServiceTickets,
  getServiceTicketById,
  changePriorityForServiceTicket,
} from "features/service/service.slice";
import { Links } from "utils/pageLinkNames";
import Header from "component/Header";
import { DataGrid } from "@mui/x-data-grid";
import DataGridTable from "component/DataGridTable";
import TableSkeleton from "component/Loaders/TableSkeleton";
import { exportToCsv } from "utils/utilFunctions";

const AllServiceTickets = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { pathname } = useLocation();
  const { mode } = useSelector((state) => state.globalState);

  const [searchParams, setSearchParams] = useSearchParams();
  const [showExportAlertDialog, setShowExportAlertDialog] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState("30");

  const { user } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState("");

  const [filters, setFilters] = useState({
    creator: "",
    assigner: "",
    technician: "",
    maintenanceCode: "",
    category: "",
    status: "",
    manufModel: "",
  });

  const [filterOptions, setFilterOptions] = useState({
    overDue: false,
    upComingIn: null,
    status: [],
    category: [],
    maintananceCode: [],
    ticketIds: [],
    manufModel: [],
  });
  const { machinesData, appConstants, isLoading, allServiceTickets } = useSelector((state) => state.service);
  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(getMachines());
  }, [dispatch, searchParams]);

  useEffect(() => {
    setLoading(true);
    dispatch(getAllServiceTickets())
      .unwrap()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, days]);

  const FilterDropdown = ({ label, value, options, onChange }) => {
    const sortedOptions = [...options].sort((a, b) => a.localeCompare(b));
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        displayEmpty
        style={{ width: "100%", fontSize: "0.875rem", fontWeight: value ? "normal" : 600,  backgroundColor: "#f5f5f5"}}
        renderValue={(selected) => selected || <span style={{ fontWeight: 600, fontSize: "0.750rem" }}>{label}</span>}
      >
        <MenuItem value="" style={{ fontWeight: 600 }}>
          {label}
        </MenuItem>
        {sortedOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    );
  };

  const ticketBelongsToPath = (ticket, pagePathName) => {
    if (!appConstants?.allTypes) return true;
    const {
      ASSIGN_SERVICE_ENGINEER,
      VENDOR_SUPPORT_REQUIRED,
      SPARES_REQUIRED,
      SPARES_RECOMMENDED,
      VENDOR_SUPPORT_PENDING,
      SPARES_REQUIRED_PENDING,
      SPARES_RECOMMENDED_PENDING,
      COMPLETED,
      NOT_COMPLETED,
      ASSIGNED,
      ON_HOLD,
      COMPLETE,
      CANCELLED,
      TIME_LOGGED,
      UNDER_OBSERVATION,
    } = appConstants.allTypes.SERVICE_TICKET_STATUS;
    const { CALIBRATION, EQUIPMENT_SUPPORT, INSPECTION, PREVENTIVE_MAINTENANCE, TASK, INTERNAL_AUDIT } =
      appConstants.allTypes.SERVICE_CATEGORY;
    const assigneUser = appConstants?.appUsers?.find((u) => ticket?.assigneeId === u.userId);

    if (pagePathName === Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS) {
      if (
        ![
          ASSIGNED,
          ON_HOLD,
          COMPLETE,
          COMPLETED,
          TIME_LOGGED,
          CANCELLED,
          SPARES_REQUIRED,
          SPARES_RECOMMENDED,
          SPARES_REQUIRED_PENDING,
          SPARES_RECOMMENDED_PENDING,
          ASSIGN_SERVICE_ENGINEER,
          UNDER_OBSERVATION,
          VENDOR_SUPPORT_REQUIRED,
          VENDOR_SUPPORT_PENDING,
          NOT_COMPLETED,
        ].includes(ticket.status)
      )
        return false;
    } else if (pagePathName === Links.ACTION_REQUIRED_SERVICE_TICKETS) {
      if (![VENDOR_SUPPORT_REQUIRED, SPARES_REQUIRED, SPARES_RECOMMENDED, COMPLETED, NOT_COMPLETED].includes(ticket.status)) return false;
    } else if (pagePathName === Links.ASSIGNED) {
      if (ticket.status !== ASSIGNED) return false;
      if (user?.roles?.[0] === appConstants?.allTypes?.USER_ROLES?.ROLE_STORES && user?.username !== assigneUser?.userName) return false;
    } else if (pagePathName === Links.SPARES_REQUIRED && ticket.status !== SPARES_REQUIRED) return false;
    else if (pagePathName === Links.SPARES_RECOMMENDED && ticket.status !== SPARES_RECOMMENDED) return false;
    else if (
      pagePathName === Links.PENDING_SERVICE_TICKETS &&
      ![VENDOR_SUPPORT_PENDING, SPARES_REQUIRED_PENDING, SPARES_RECOMMENDED_PENDING].includes(ticket.status)
    )
      return false;
    else if (
      pagePathName === Links.PERIODIC_SERVICE_TICKETS &&
      ![CALIBRATION, INSPECTION, INTERNAL_AUDIT, PREVENTIVE_MAINTENANCE].includes(ticket.ticketCategory)
    )
      return false;
    else if (pagePathName === Links.ONE_TIME_SERVICE_TICKETS && ![EQUIPMENT_SUPPORT, TASK].includes(ticket.ticketCategory)) return false;
    return true;
  };

  const rawTicketsForPath = useMemo(() => {
    const data = allServiceTickets?.data || [];
    const pagePathName = pathname.substring(1);
    const daysNumber = parseInt(days, 10);
    const cutoffDate = moment().subtract(daysNumber, "days").startOf("day");

    return data
      .filter((ticket) => ticketBelongsToPath(ticket, pagePathName))
      .filter((ticket) => {
        if (!ticket.openDate) return true;
        return moment(ticket.openDate).isSameOrAfter(cutoffDate);
      });
  }, [allServiceTickets?.data, pathname, appConstants, user, days]);

  const filteredTickets = useMemo(() => {
    let tickets = rawTicketsForPath || [];

    if (process.env.NODE_ENV === "development") {
      console.log("[Filter] Starting filter run", {
        path: pathname.substring(1),
        baseCount: tickets.length,
        filters,
        filterOptions,
        searchValue,
      });
    }

    if (filters.creator) {
      const before = tickets.length;
      tickets = tickets.filter((t) => {
        const creatorUser = appConstants?.appUsers?.find((u) => t.creatorId === u.userId);
        return (creatorUser?.name || "") === filters.creator;
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After creator filter", filters.creator, "count:", tickets.length, "from:", before);
      }
    }
    if (filters.assigner) {
      const before = tickets.length;
      tickets = tickets.filter((t) => {
        const assignerUser = appConstants?.appUsers?.find((u) => t.assignerId === u.userId);
        // Match what we show in the Supervisor header dropdown (name)
        return (assignerUser?.name || assignerUser?.nameEmpno || "") === filters.assigner;
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After supervisor filter", filters.assigner, "count:", tickets.length, "from:", before);
      }
    }
    if (filters.technician) {
      const before = tickets.length;
      tickets = tickets.filter((t) => {
        const assigneUser = appConstants?.appUsers?.find((u) => t.assigneeId === u.userId);
        // Match what we show in the Tech header dropdown (name)
        return (assigneUser?.name || assigneUser?.nameEmpno || "") === filters.technician;
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After technician filter", filters.technician, "count:", tickets.length, "from:", before);
      }
    }
    if (filters.maintenanceCode) {
      const before = tickets.length;
      tickets = tickets.filter((t) => (t.maintenanceCode || "") === filters.maintenanceCode);
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After maintenance code filter", filters.maintenanceCode, "count:", tickets.length, "from:", before);
      }
    }
    if (filters.category) {
      const before = tickets.length;
      tickets = tickets.filter((t) => (t.ticketCategory || "") === filters.category);
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After category filter", filters.category, "count:", tickets.length, "from:", before);
      }
    }
    if (filters.status) {
      const before = tickets.length;
      tickets = tickets.filter((t) => (t.status || "") === filters.status);
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After status filter", filters.status, "count:", tickets.length, "from:", before);
      }
    }
    if (filters.manufModel) {
      const before = tickets.length;
      tickets = tickets.filter((t) => {
        const combined = t.manufacturer && t.modelName ? `${t.manufacturer} - ${t.modelName}` : t.manufacturer || t.modelName || "";
        return combined === filters.manufModel;
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After Manf & Model filter", filters.manufModel, "count:", tickets.length, "from:", before);
      }
    }
    if (filterOptions.overDue) {
      const before = tickets.length;
      tickets = tickets.filter((t) => t.dueDate && moment(moment().format("DD MMM YYYY")).isAfter(moment(t.dueDate).format("DD MMM YYYY")));
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After Overdue filter count:", tickets.length, "from:", before);
      }
    }
    if (filterOptions.upComingIn != null) {
      const before = tickets.length;
      tickets = tickets.filter(
        (t) => t.dueDate && moment(t.dueDate).isBetween(moment(), moment().add(parseInt(filterOptions.upComingIn), "d")),
      );
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After UpcomingIn filter", filterOptions.upComingIn, "days, count:", tickets.length, "from:", before);
      }
    }
    if (filterOptions.ticketIds?.length) {
      const before = tickets.length;
      tickets = tickets.filter((t) => filterOptions.ticketIds.includes(t.ticketId?.toString()));
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After TicketIds filter", filterOptions.ticketIds, "count:", tickets.length, "from:", before);
      }
    }
    if (searchValue.trim()) {
      const before = tickets.length;
      const searchLower = searchValue.toLowerCase();
      tickets = tickets.filter((t) => {
        const assigneUser = appConstants?.appUsers?.find((u) => t.assigneeId === u.userId);
        const assignerUser = appConstants?.appUsers?.find((u) => t.assignerId === u.userId);
        const creatorUser = appConstants?.appUsers?.find((u) => t.creatorId === u.userId);
        const searchFields = [
          t.ticketId?.toString(),
          t.ticketNumber?.toString(),
          t.description,
          t.maintenanceCode,
          t.manufacturer,
          t.modelName,
          assigneUser?.name,
          assignerUser?.name,
          creatorUser?.name,
        ];
        return searchFields.some((field) => field?.toLowerCase().includes(searchLower));
      });
      if (process.env.NODE_ENV === "development") {
        console.log("[Filter] After search filter", searchValue, "count:", tickets.length, "from:", before);
      }
    }

    if (process.env.NODE_ENV === "development") {
      console.log("[Filter] Final filtered tickets count:", tickets.length);
    }

    return tickets;
  }, [rawTicketsForPath, filters, filterOptions.overDue, filterOptions.upComingIn, filterOptions.ticketIds, searchValue, appConstants]);

  const transformTicket = (ticket) => {
    const assigneUser = appConstants?.appUsers?.find((u) => ticket?.assigneeId === u.userId);
    const assignerUser = appConstants?.appUsers?.find((u) => ticket?.assignerId === u.userId);
    const creatorUser = appConstants?.appUsers?.find((u) => ticket?.creatorId === u.userId);
    return {
      ticketId: ticket?.ticketId,
      ticketNumber: ticket?.ticketNumber,
      ticketCategory: ticket.ticketCategory,
      status: ticket.status,
      machineManufacturer: ticket?.manufacturer || "",
      machineModel: ticket?.modelName || "",
      machineModelId: ticket?.modelId || "",
      machineId: ticket?.machineId || "",
      machineSerialNumber: ticket?.serialNumber || "",
      machineMaintenanceCode: ticket?.maintenanceCode || "",
      description: ticket?.description || "",
      dueDate: ticket.dueDate ? moment(ticket.dueDate).format("DD MMM YY") : null,
      dateCreated: moment(ticket.openDate).format("DD MMM YY"),
      scheduledDate: moment(ticket.scheduledDate).format("DD MMM YY"),
      priority: ticket.priority || null,
      vendor: ticket.vendor || null,
      technician: assigneUser?.name,
      assigner: assignerUser?.name,
      creator: creatorUser?.name,
      safetyTicket: ticket?.safetyTicket ?? null,
    };
  };

  const transformedTickets = useMemo(() => {
    return (filteredTickets || []).map(transformTicket).sort((a, b) => b.ticketId - a.ticketId);
  }, [filteredTickets, appConstants]);

  const serviceTicketCount = useMemo(() => {
    const st = transformedTickets || [];
    const {
      COMPLETED,
      SPARES_REQUIRED,
      SPARES_RECOMMENDED,
      VENDOR_SUPPORT_REQUIRED,
      NOT_COMPLETED,
      SPARES_REQUIRED_PENDING,
      SPARES_RECOMMENDED_PENDING,
      VENDOR_SUPPORT_PENDING,
      ASSIGN_SERVICE_ENGINEER,
      ASSIGNED,
    } = appConstants?.allTypes?.SERVICE_TICKET_STATUS || {};
    const cmp = st.filter((t) => t.status === COMPLETED);
    const rqrd = st.filter((t) => t.status === SPARES_REQUIRED);
    const rcmd = st.filter((t) => t.status === SPARES_RECOMMENDED);
    const vendor = st.filter((t) => t.status === VENDOR_SUPPORT_REQUIRED);
    const requiredPending = st.filter((t) => t.status === SPARES_REQUIRED_PENDING);
    const recommendedPending = st.filter((t) => t.status === SPARES_RECOMMENDED_PENDING);
    const notCompleted = st.filter((t) => t.status === NOT_COMPLETED);
    const vendorPending = st.filter((t) => t.status === VENDOR_SUPPORT_PENDING);
    const allTickets = st.filter((t) =>
      [COMPLETED, SPARES_REQUIRED, SPARES_RECOMMENDED, VENDOR_SUPPORT_REQUIRED, NOT_COMPLETED].includes(t.status),
    );
    const allpendingtickets = st.filter((t) =>
      [SPARES_REQUIRED_PENDING, SPARES_RECOMMENDED_PENDING, VENDOR_SUPPORT_PENDING].includes(t.status),
    );
    const activeTabTickets = st.filter((t) => t.status === ASSIGNED);
    const safetyTicket = st.filter(
      (t) => t.safetyTicket === true && t.status !== appConstants?.allTypes?.SERVICE_TICKET_STATUS?.SUPERVISOR_APPROVED,
    );
    return {
      completedCount: cmp,
      sparesRequired: rqrd,
      sparesRecommended: rcmd,
      vendorSupport: vendor,
      allActionRequired: allTickets,
      sparesRequiredpendng: requiredPending,
      sparesRecommendedPending: recommendedPending,
      vendorSupportPending: vendorPending,
      notCompleted,
      allpendingticket: allpendingtickets,
      safetyTicket,
      assignTech: st.filter((t) => t.status === ASSIGN_SERVICE_ENGINEER),
      high: activeTabTickets.filter((t) => t.priority === appConstants?.allTypes?.SERVICE_TICKET_PRIORITY?.HIGH),
      low: activeTabTickets.filter((t) => t.priority === appConstants?.allTypes?.SERVICE_TICKET_PRIORITY?.LOW),
      medium: activeTabTickets.filter((t) => t.priority === appConstants?.allTypes?.SERVICE_TICKET_PRIORITY?.MEDIUM),
      vendorTicket: activeTabTickets.filter((t) => t.vendor != null),
    };
  }, [transformedTickets, appConstants]);

  const filteredServiceTickets = useMemo(() => {
    switch (activeTab) {
      case "completed":
        return serviceTicketCount.completedCount;
      case "sparesRequired":
        return serviceTicketCount.sparesRequired;
      case "sparesRecommended":
        return serviceTicketCount.sparesRecommended;
      case "vendorSupportRequired":
        return serviceTicketCount.vendorSupport;
      case "sparesRequiredPending":
        return serviceTicketCount.sparesRequiredpendng;
      case "sparesRecommendedPending":
        return serviceTicketCount.sparesRecommendedPending;
      case "vendorSupportPending":
        return serviceTicketCount.vendorSupportPending;
      case "allAction":
        return serviceTicketCount.allActionRequired;
      case "notCompleted":
        return serviceTicketCount.notCompleted;
      case "allPending":
        return serviceTicketCount.allpendingticket;
      case "assignTech":
        return serviceTicketCount.assignTech;
      case "safetyTicket":
        return serviceTicketCount.safetyTicket;
      case "high":
        return serviceTicketCount.high;
      case "low":
        return serviceTicketCount.low;
      case "medium":
        return serviceTicketCount.medium;
      case "vendorTicket":
        return serviceTicketCount.vendorTicket;
      default:
        return transformedTickets;
    }
  }, [activeTab, serviceTicketCount, transformedTickets]);

  const { creators, supervisors, technicians, maintenanceCodes, categories, statuses, manufModels } = useMemo(() => {
    const creatorsSet = new Set();
    const supervisorsSet = new Set();
    const techniciansSet = new Set();
    const maintenanceCodesSet = new Set();
    const categoriesSet = new Set();
    const statusesSet = new Set();
    const manufModelsSet = new Set();
    (filteredServiceTickets || []).forEach((row) => {
      if (row.creator) creatorsSet.add(row.creator);
      if (row.assigner) supervisorsSet.add(row.assigner);
      if (row.technician) techniciansSet.add(row.technician);
      if (row.machineMaintenanceCode) maintenanceCodesSet.add(row.machineMaintenanceCode);
      if (row.ticketCategory) categoriesSet.add(row.ticketCategory);
      if (row.status) statusesSet.add(row.status);
      if (row.machineManufacturer && row.machineModel) manufModelsSet.add(`${row.machineManufacturer} - ${row.machineModel}`);
    });
    const creatorsArr = Array.from(creatorsSet).sort((a, b) => a.localeCompare(b));
    const supervisorsArr = Array.from(supervisorsSet).sort((a, b) => a.localeCompare(b));
    const techniciansArr = Array.from(techniciansSet).sort((a, b) => a.localeCompare(b));
    const maintenanceCodesArr = Array.from(maintenanceCodesSet).sort((a, b) => a.localeCompare(b));
    const categoriesArr = Array.from(categoriesSet).sort((a, b) => a.localeCompare(b));
    const statusesArr = Array.from(statusesSet).sort((a, b) => a.localeCompare(b));
    const manufModelsArr = Array.from(manufModelsSet).sort((a, b) => a.localeCompare(b));

    if (process.env.NODE_ENV === "development") {
      console.log("[Header Options] Creators:", creatorsArr);
      console.log("[Header Options] Supervisors:", supervisorsArr);
      console.log("[Header Options] Technicians:", techniciansArr);
    }

    return {
      creators: creatorsArr,
      supervisors: supervisorsArr,
      technicians: techniciansArr,
      maintenanceCodes: maintenanceCodesArr,
      categories: categoriesArr,
      statuses: statusesArr,
      manufModels: manufModelsArr,
    };
  }, [filteredServiceTickets]);

  useEffect(() => {
    if (pathname.substring(1) === Links.ACTION_REQUIRED_SERVICE_TICKETS) {
      setActiveTab("completed");
    } else if (pathname.substring(1) === Links.PENDING_SERVICE_TICKETS) {
      setActiveTab("sparesRequiredPending");
    } else if (pathname.substring(1) === Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS) {
      setActiveTab("safetyTicket");
    }
  }, [pathname]);

  useEffect(() => {
    const ticketCategoryArrayParam = searchParams.getAll("ticketCategory[]");
    const maintenanceCodeArrayParam = searchParams.getAll("maintenanceCode[]");
    const ticketStatusArrayParam = searchParams.getAll("ticketStatus[]");
    const ticketIdArrayParam = searchParams.getAll("ticketId[]");
    const manufModelArrayParam = searchParams.getAll("manufModel[]");
    setFilterOptions((prev) => ({
      ...prev,
      category: ticketCategoryArrayParam,
      maintananceCode: maintenanceCodeArrayParam,
      status: ticketStatusArrayParam,
      ticketIds: ticketIdArrayParam,
      manufModel: manufModelArrayParam,
    }));
    setFilters((prev) => ({
      ...prev,
      category: ticketCategoryArrayParam[0] || "",
      maintenanceCode: maintenanceCodeArrayParam[0] || "",
      status: ticketStatusArrayParam[0] || "",
      manufModel: manufModelArrayParam[0] || "",
    }));
  }, []);

  const updatePriority = (ticketId, newPriority) => {
    const updateObj = {
      serviceTktId: ticketId,
      Priority: newPriority,
    };
    dispatch(changePriorityForServiceTicket(updateObj)).then((res) => {
      if (res && res.error) {
        toast.error("Failed to update priority!");
      } else if (res && res.payload?.status === 200) {
        toast.success(`Priority updated to ${newPriority} successfully!`, {
          autoClose: 1000,
        });
        dispatch(getAllServiceTickets());
      } else {
        toast.error("Something went wrong!");
      }
    });
  };

  const renderGridActions = (data) => {
    return (
      <div>
        {/* <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + data.row.ticketId)}>
          <Tooltip title="View">
            <ViewIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton> */}
        <IconButton
          sx={{ mr: 1 }}
          onClick={() => navigate(`/${Links.UPDATE_SERVICE_TICKET}/` + data.row.ticketId, { state: { enableEditing: true } })}
        >
          <Tooltip title="Edit">
            <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
          </Tooltip>
        </IconButton>
        {data.row.priority === "High" && (
          <Tooltip title="Medium Priority">
            <IconButton sx={{ mr: 1 }} onClick={() => updatePriority(data.row.ticketId, "Medium")}>
              <ArrowDownwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </IconButton>
          </Tooltip>
        )}
        {data.row.priority === "Medium" && (
          <span>
            <Tooltip title="High Priority">
              <IconButton onClick={() => updatePriority(data.row.ticketId, "High")}>
                <ArrowUpwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Low Priority">
              <IconButton onClick={() => updatePriority(data.row.ticketId, "Low")}>
                <ArrowDownwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </IconButton>
            </Tooltip>
          </span>
        )}

        {data.row.priority === "Low" && (
          <Tooltip title="Medium Priority">
            <IconButton onClick={() => updatePriority(data.row.ticketId, "Medium")}>
              <ArrowUpwardIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
            </IconButton>
          </Tooltip>
        )}
        {/* {appConstants && Object.keys(appConstants).length && data.row.status === appConstants.allTypes.SERVICE_TICKET_STATUS.COMPLETED ? (
          <Button variant="contained" onClick={() => onClickApprove(data.row)} sx={{ ml: 1 }}>
            Approve
          </Button>
        ) : null} */}
      </div>
    );
  };

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTableData = () => {
    return (
      <Box style={{ height: 700, width: "100%", paddingTop: 3, marginTop: 8, overflow: "auto" }}>
        {loading || isLoading ? (
          <TableSkeleton rows={8} columns={columnAttributes.length} />
        ) : (
          <DataGrid
            loading={loading}
            getRowId={(row) => row.ticketId}
            rows={filteredServiceTickets}
            columns={columnAttributes}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableColumnMenu
            localeText={{ noRowsLabel: "No tickets." }}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },
            }}
          />
        )}
      </Box>
    );
  };

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const columnAttributes = [
    {
      field: "ticketNumber",
      headerName: "Ticket ID",
      minWidth: 50,
      flex: 0.38,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.ticketId : params.row.ticketNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.ticketId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.ticketId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Open Date",
      minWidth: 90,
      flex: 0.3,
      sortComparator: dateComparator,
    },
    {
      field: "scheduledDate",
      headerName: "Sch. Date",
      minWidth: 90,
      flex: 0.3,
      sortComparator: dateComparator,
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      minWidth: 90,
      flex: 0.3,
      sortComparator: dateComparator,
    },
    {
      field: "creator",
      headerName: "Creator",
      minWidth: 120,
      flex: 0.6,
      renderHeader: () => (
        <FilterDropdown
          label="Creator"
          value={filters.creator}
          options={creators}
          onChange={(value) => setFilters({ ...filters, creator: value })}
        />
      ),
    },
    {
      field: "assigner",
      headerName: "Supervisor",
      minWidth: 130,
      flex: 0.5,
      renderHeader: () => (
        <FilterDropdown
          label="Supervisor"
          value={filters.assigner}
          options={supervisors}
          onChange={(value) => setFilters({ ...filters, assigner: value })}
        />
      ),
    },
    {
      field: "technician",
      headerName: "Tech",
      minWidth: 120,
      flex: 0.5,
      renderHeader: () => (
        <FilterDropdown
          label="Tech"
          value={filters.technician}
          options={technicians}
          onChange={(value) => setFilters({ ...filters, technician: value })}
        />
      ),
    },
    ...(appConstants?.showManufacturerModelInAssetList === true
      ? [
          {
            field: "manufModel",
            headerName: "Manf & Model",
            minWidth: 130,
            flex: 0.7,
            renderHeader: () => (
              <FilterDropdown
                label="Manf & Model"
                value={filters.manufModel}
                options={manufModels}
                onChange={(value) => setFilters({ ...filters, manufModel: value })}
              />
            ),
            renderCell: (params) => {
              const manufacturer = params.row.machineManufacturer || "";
              const model = params.row.machineModel || "";
              const combinedValue = manufacturer && model ? `${manufacturer} - ${model}` : manufacturer || model;

              return (
                <div
                  onClick={() => navigate(`/${Links.MACHINE_MODEL}/${params.row.machineModelId}`)}
                  style={{ cursor: "pointer", textDecoration: "none" }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                >
                  {combinedValue}
                </div>
              );
            },
            valueGetter: (params) => {
              const manufacturer = params.row.machineManufacturer || "";
              const model = params.row.machineModel || "";
              return manufacturer && model ? `${manufacturer} - ${model}` : manufacturer || model;
            },
          },
        ]
      : []),
    {
      field: "machineMaintenanceCode",
      headerName: "Maint. Code",
      minWidth: 100,
      flex: 0.6,
      editType: "dropdownedit",
      textAlign: "left",
      renderHeader: () => (
        <FilterDropdown
          label="Maint. Code"
          value={filters.maintenanceCode}
          options={maintenanceCodes}
          onChange={(value) => setFilters({ ...filters, maintenanceCode: value })}
        />
      ),
      renderCell: (params) => {
        return (
          <div
            onClick={() => navigate(`/${Links.EDIT_MACHINE}/${params.row.machineId}`)}
            style={{ cursor: "pointer", textDecoration: "none" }}
            onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            {params.value}
          </div>
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 100,
      editType: "dropdownedit",
      textAlign: "left",
      flex: 0.4,
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 90,
      flex: 0.5,
      renderCell: (params) => {
        const abbreviation = appConstants?.allTypes?.ABBREVIATIONS?.[params.value];
        return abbreviation || params.value;
      },
      renderHeader: () => {
        const abbreviatedOptions = categories.map((cat) => appConstants?.allTypes?.ABBREVIATIONS?.[cat] || cat);
        const displayValue = filters.category ? appConstants?.allTypes?.ABBREVIATIONS?.[filters.category] || filters.category : "";
        return (
          <FilterDropdown
            label="Category"
            value={displayValue}
            options={abbreviatedOptions}
            onChange={(abbreviatedValue) => {
              if (!abbreviatedValue) {
                setFilters({ ...filters, category: "" });
                return;
              }
              const originalCategory =
                categories.find((cat) => appConstants?.allTypes?.ABBREVIATIONS?.[cat] === abbreviatedValue) || abbreviatedValue;
              setFilters({ ...filters, category: originalCategory });
            }}
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 90,
      flex: 0.5,
      renderHeader: () => (
        <FilterDropdown
          label="Status"
          value={filters.status}
          options={statuses}
          onChange={(value) => setFilters({ ...filters, status: value })}
        />
      ),
    },
    {
      field: "vendor",
      headerName: "Vendor",
      minWidth: 90,
      flex: 0.3,
      valueGetter: (params) => {
        const vendorId = params.row?.vendor?.id || params.row?.vendor;
        const customer = appConstants.customers.find((user) => user.id === vendorId);
        return customer?.customerName || "Internal";
      },
    },
    {
      field: "Actions",
      headerName: "Actions",
      width: 140,
      flex: 0.8,
      sortable: false,
      filterable: false,
      renderCell: renderGridActions,
      disableClickEventBubbling: true,
    },
  ];

  const hasActiveFilters = () =>
    searchValue.trim() !== "" ||
    filterOptions.overDue ||
    filterOptions.upComingIn != null ||
    (filterOptions.ticketIds && filterOptions.ticketIds.length > 0) ||
    Object.values(filters).some((v) => v !== "");

  useEffect(() => {
    setFilters({ creator: "", assigner: "", technician: "", maintenanceCode: "", category: "", status: "", manufModel: "" });
    setFilterOptions({ overDue: false, upComingIn: null, status: [], category: [], maintananceCode: [], ticketIds: [], manufModel: [] });
  }, [activeTab]);

  const handleClearAll = () => {
    setFilters({ creator: "", assigner: "", technician: "", maintenanceCode: "", category: "", status: "", manufModel: "" });
    setFilterOptions({ overDue: false, upComingIn: null, status: [], category: [], maintananceCode: [], ticketIds: [], manufModel: [] });
    setSearchParams({});
  };

  const FILTER_LABELS = {
    creator: "Creator",
    assigner: "Supervisor",
    technician: "Tech",
    maintenanceCode: "Maint. Code",
    category: "Category",
    status: "Status",
    manufModel: "Manf & Model",
  };

  const renderAppliedHeaderFilters = () => {
    const entries = Object.entries(filters).filter(([, value]) => value !== "");
    if (entries.length === 0) return null;
    return (
      <Paper>
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          {entries.map(([key, value]) => {
            const displayValue =
              key === "category" && appConstants?.allTypes?.ABBREVIATIONS?.[value] ? appConstants.allTypes.ABBREVIATIONS[value] : value;
            return (
              <React.Fragment key={key}>
                <Typography m={1} textTransform={"capitalize"}>
                  {FILTER_LABELS[key]}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
                  <Typography variant="caption">{displayValue}</Typography>
                  <IconButton onClick={() => setFilters((prev) => ({ ...prev, [key]: "" }))}>
                    <Tooltip title="Clear item">
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Tooltip>
                  </IconButton>
                </Stack>
              </React.Fragment>
            );
          })}
        </Stack>
      </Paper>
    );
  };

  const downloadCsv = (e, exportAll = false) => {
    e.preventDefault();
    const serviceTicketsList = [];
    if (exportAll) {
      const baseTickets = rawTicketsForPath || [];
      const pagePathName = pathname.substring(1);
      let ticketsForExport = baseTickets;

      if (appConstants?.allTypes) {
        const statusConstants = appConstants.allTypes.SERVICE_TICKET_STATUS || {};
        const priorityConstants = appConstants.allTypes.SERVICE_TICKET_PRIORITY || {};

        if (pagePathName === Links.ACTION_REQUIRED_SERVICE_TICKETS) {
          const { COMPLETED, SPARES_REQUIRED, SPARES_RECOMMENDED, VENDOR_SUPPORT_REQUIRED, NOT_COMPLETED } = statusConstants;
          switch (activeTab) {
            case "completed":
              ticketsForExport = baseTickets.filter((t) => t.status === COMPLETED);
              break;
            case "sparesRequired":
              ticketsForExport = baseTickets.filter((t) => t.status === SPARES_REQUIRED);
              break;
            case "sparesRecommended":
              ticketsForExport = baseTickets.filter((t) => t.status === SPARES_RECOMMENDED);
              break;
            case "vendorSupportRequired":
              ticketsForExport = baseTickets.filter((t) => t.status === VENDOR_SUPPORT_REQUIRED);
              break;
            case "notCompleted":
              ticketsForExport = baseTickets.filter((t) => t.status === NOT_COMPLETED);
              break;
            case "allAction":
            default:
              ticketsForExport = baseTickets.filter((t) =>
                [COMPLETED, SPARES_REQUIRED, SPARES_RECOMMENDED, VENDOR_SUPPORT_REQUIRED, NOT_COMPLETED].includes(t.status),
              );
              break;
          }
        } else if (pagePathName === Links.PENDING_SERVICE_TICKETS) {
          const { SPARES_REQUIRED_PENDING, SPARES_RECOMMENDED_PENDING, VENDOR_SUPPORT_PENDING } = statusConstants;
          switch (activeTab) {
            case "sparesRequiredPending":
              ticketsForExport = baseTickets.filter((t) => t.status === SPARES_REQUIRED_PENDING);
              break;
            case "sparesRecommendedPending":
              ticketsForExport = baseTickets.filter((t) => t.status === SPARES_RECOMMENDED_PENDING);
              break;
            case "vendorSupportPending":
              ticketsForExport = baseTickets.filter((t) => t.status === VENDOR_SUPPORT_PENDING);
              break;
            case "allPending":
            default:
              ticketsForExport = baseTickets.filter((t) =>
                [SPARES_REQUIRED_PENDING, SPARES_RECOMMENDED_PENDING, VENDOR_SUPPORT_PENDING].includes(t.status),
              );
              break;
          }
        } else if (pagePathName === Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS) {
          const { ASSIGN_SERVICE_ENGINEER, ASSIGNED, SUPERVISOR_APPROVED } = statusConstants;
          const { HIGH, MEDIUM, LOW } = priorityConstants;
          switch (activeTab) {
            case "safetyTicket":
              ticketsForExport = baseTickets.filter((t) => t.safetyTicket === true && t.status !== SUPERVISOR_APPROVED);
              break;
            case "assignTech":
              ticketsForExport = baseTickets.filter((t) => t.status === ASSIGN_SERVICE_ENGINEER);
              break;
            case "high":
              ticketsForExport = baseTickets.filter((t) => t.status === ASSIGNED && t.priority === HIGH);
              break;
            case "medium":
              ticketsForExport = baseTickets.filter((t) => t.status === ASSIGNED && t.priority === MEDIUM);
              break;
            case "low":
              ticketsForExport = baseTickets.filter((t) => t.status === ASSIGNED && t.priority === LOW);
              break;
            case "vendorTicket":
              ticketsForExport = baseTickets.filter((t) => t.status === ASSIGNED && t.vendor != null);
              break;
            default:
              ticketsForExport = baseTickets;
              break;
          }
        } else {
          ticketsForExport = baseTickets;
        }
      }

      ticketsForExport.forEach((ticket) => {
        const machine = machinesData.find((machine) => machine.id === ticket?.machine?.id);
        const assigneUser = appConstants?.appUsers?.find((user) => {
          return ticket?.assignees?.[0] === user.userId;
        });
        const assignerUser = appConstants?.appUsers?.find((user) => {
          return ticket?.assignerId === user.userId;
        });
        const creatorUser = appConstants?.appUsers?.find((user) => {
          return ticket?.creatorId === user.userId;
        });

        return serviceTicketsList.push({
          ticketId: ticket?.ticketId,
          ticketNumber: appConstants?.showServiceTicketId ? ticket?.ticketId : ticket?.ticketNumber || ticket?.ticketId,
          ticketCategory: ticket?.ticketCategory,
          status: ticket?.status,
          machineManufacturer: ticket?.manufacturer || "",
          machineModel: ticket?.model || "",
          machineSerialNumber: ticket?.serialNumber || "",
          machineMaintenanceCode: ticket?.maintenanceCode || "",
          description: ticket?.description || "",
          dueDate: moment(ticket.dueDate).format("DD MMM YYYY"),
          dateCreated: moment(ticket.openDate).format("DD MMM YYYY"),
          scheduledDate: moment(ticket.scheduledDate).format("DD MMM YYYY"),
          technician: assigneUser?.name,
          assigner: assignerUser?.name,
          creator: creatorUser?.name,
        });
      });
    }
    const headers = [
      "Ticket ID,Open Date, Scheduled Date, Maintenance Code,Description, Manufacturer,Model,Serial Number,Creator,Supervisor,Technician,Category, Status",
    ];
    const keys = [
      "ticketNumber",
      "dateCreated",
      "scheduledDate",
      "machineMaintenanceCode",
      "description",
      "machineManufacturer",
      "machineModel",
      "machineSerialNumber",
      "creator",
      "assigner",
      "technician",
      "ticketCategory",
      "status",
    ];
    const dataToExport = exportAll
      ? serviceTicketsList
      : filteredServiceTickets.map((ticket) => ({
          ...ticket,
          ticketNumber: appConstants?.showServiceTicketId ? ticket.ticketId : ticket.ticketNumber || ticket.ticketId,
        }));
    exportToCsv(headers, keys, dataToExport, "ServiceTickets");
    setShowExportAlertDialog(false);
  };
  const filterTickets = (filterOption, filterItem) => {
    setFilterOptions({ ...filterOptions, [filterOption]: filterOptions[filterOption].filter((item) => item !== filterItem) });
  };
  const renderCustomFilter = (filterOption) => (
    <Paper>
      {filterOptions &&
      filterOptions[filterOption] &&
      filterOptions[filterOption].length &&
      filterOption !== "category" &&
      filterOption !== "status" &&
      filterOption !== "maintenanceCode" &&
      filterOption !== "ticketIds" &&
      filterOption !== "priority" ? (
        <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} px={0.5} py={filterOptions && filterOptions.overDue ? 0 : 0.3}>
          <Typography m={1} textTransform={"capitalize"}>
            {filterOption}
          </Typography>
          {filterOptions[filterOption].map((filterItem) => (
            <Stack direction={"row"} alignItems={"center"} pl={1} borderRadius={10} backgroundColor="#00808030">
              <Typography variant="caption">{filterItem}</Typography>
              <IconButton onClick={() => filterTickets(filterOption, filterItem)}>
                <Tooltip title="Clear item">
                  <CloseIcon sx={{ fontSize: 15 }} />
                </Tooltip>
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </Paper>
  );
  const showFilters = ![
    Links.ACTION_REQUIRED_SERVICE_TICKETS,
    Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS,
    Links.PENDING_SERVICE_TICKETS,
  ].includes(pathname.substring(1));

  return (
    <>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header
          titles={[
            (Links.ACTION_REQUIRED_SERVICE_TICKETS === pathname.substring(1)
              ? "Action Required "
              : Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS === pathname.substring(1)
                ? "Active "
                : Links.PENDING_SERVICE_TICKETS === pathname.substring(1)
                  ? "Pending "
                  : Links.ASSIGNED === pathname.substring(1)
                    ? "Assigned "
                    : Links.SPARES_REQUIRED === pathname.substring(1)
                      ? "Spares Required "
                      : Links.SPARES_RECOMMENDED === pathname.substring(1)
                        ? "Spares Recommended "
                        : Links.PERIODIC_SERVICE_TICKETS === pathname.substring(1)
                          ? "Periodic "
                          : "One Time ") + "Service Tickets",
          ]}
        />
      </Stack>

      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} justifyContent={"space-between"} my={2}>
          <Stack direction={"row"} flexWrap={"wrap"} alignItems={"center"} gap={2}>
            {pathname !== `/${Links.ASSIGNED}` &&
              pathname !== `/${Links.SPARES_REQUIRED}` &&
              pathname !== `/${Links.SPARES_RECOMMENDED}` && (
                <Box>
                  <Button
                    startIcon={<AddIcon />}
                    sx={{ height: "self" }}
                    autoCapitalize="none"
                    variant="contained"
                    onClick={() =>
                      navigate(`/${Links.CREATE_SERVICE_TICKET}/`, {
                        state: { isCreatingTicket: true },
                      })
                    }
                  >
                    Create
                  </Button>
                </Box>
              )}

            <OutlinedInput
              id="outlined-adornment-password"
              value={searchValue}
              onChange={(event) => handleSearchChange(event)}
              placeholder="Search..."
              endAdornment={
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" edge="end">
                    <Search />
                  </IconButton>
                </InputAdornment>
              }
              sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
            />

            {showFilters && (
              <Paper>
                <Stack
                  direction={"row"}
                  backgroundColor={
                    filterOptions && filterOptions.overDue
                      ? mode === "dark"
                        ? theme.palette.grey[700]
                        : theme.palette.primary[700]
                      : mode === "dark"
                        ? theme.palette.grey[10]
                        : theme.palette.primary[200]
                  }
                  alignItems={"center"}
                  px={0.5}
                  py={filterOptions && filterOptions.overDue ? 0 : 0.3}
                >
                  <Tooltip title="Filter over due tickets">
                    <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: true })} size="small">
                      <Typography sx={{ color: "#fff" }}>Over due</Typography>
                    </IconButton>
                  </Tooltip>
                  {filterOptions && filterOptions.overDue ? (
                    <IconButton onClick={() => setFilterOptions({ ...filterOptions, overDue: false })}>
                      <Tooltip title="Clear filter">
                        <CloseIcon sx={{ fontSize: 18, color: "#fff" }} />
                      </Tooltip>
                    </IconButton>
                  ) : null}
                </Stack>
              </Paper>
            )}
            <Paper sx={{ px: 1, py: 0.7 }}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>Days</Typography>
                {[
                  { label: "1M", value: "30" },
                  { label: "2M", value: "60" },
                  { label: "3M", value: "90" },
                  { label: "6M", value: "180" },
                  { label: "1Y", value: "365" },
                ].map((opt) => {
                  const isActive = days === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      size="small"
                      variant={isActive ? "contained" : "outlined"}
                      onClick={() => setDays(opt.value)}
                      sx={{
                        minWidth: 45,
                        height: 28,
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Stack>
            </Paper>
            {/* {showFilters && (
              <Autocomplete
                disablePortal
                size="small"
                id="combo-box-demo"
                onChange={(event, newValue, reason) => {
                  setFilterOptions({ ...filterOptions, upComingIn: reason === "clear" ? null : parseInt(newValue) });
                }}
                value={filterOptions && filterOptions.upComingIn ? filterOptions.upComingIn : ""}
                options={[7, 15, 30, 60, 90]}
                sx={{ minWidth: 150, maxWidth: 600 }}
                renderInput={(params) => <TextField {...params} label="Upcoming in" style={{}} />}
              />
            )} */}
            <Button startIcon={<CloseIcon />} sx={{ height: "self" }} autoCapitalize="none" variant="contained" onClick={handleClearAll}>
              Clear All
            </Button>
            {renderAppliedHeaderFilters()}
            {/* <Autocomplete
              disablePortal
              size="small"
              id="combo-box-demo"
              isOptionEqualToValue={(option, value) => option.nameEmpno == value.nameEmpno}
              getOptionLabel={(option) => option.nameEmpno}
              onChange={(event, newValue, reason) => {
                setFilterOptions({ ...filterOptions, technician: reason === "clear" ? null : newValue });
              }}
              value={filterOptions.technician || null}
              options={appConstants?.appUsers.filter((user) => user.userRole === appConstants.allTypes.USER_ROLES.ROLE_SERVICE_TECHNICIAN)}
              sx={{ minWidth: 300, maxWidth: 500 }}
              renderInput={(params) => <TextField {...params} label="Technician" />}
            /> */}
            {/* <Autocomplete
              disablePortal
              size="small"
              id="combo-box-demo"
              isOptionEqualToValue={(option, value) => option.nameEmpno == value.nameEmpno}
              getOptionLabel={(option) => option.nameEmpno}
              onChange={(event, newValue, reason) => {
                setFilterOptions({ ...filterOptions, assigner: reason === "clear" ? null : newValue });
              }}
              value={filterOptions.assigner || null}
              options={appConstants?.appUsers.filter(
                (user) => user.userRole === appConstants.allTypes.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR
              )}
              sx={{ minWidth: 300, maxWidth: 500 }}
              renderInput={(params) => <TextField {...params} label="Supervisor" />}
            /> */}

            {Object.keys(filterOptions).map((filterOption) => renderCustomFilter(filterOption))}
          </Stack>
          <Stack>
            <Button
              startIcon={<DownloadIcon />}
              sx={{ minWidth: 170 }}
              autoCapitalize="none"
              variant="contained"
              disabled={!filteredServiceTickets?.length}
              onClick={(e) => {
                if (hasActiveFilters()) setShowExportAlertDialog(true);
                else downloadCsv(e);
              }}
            >
              Export to csv
            </Button>
          </Stack>
        </Stack>

        {pathname.substring(1) === Links.ACTION_REQUIRED_SERVICE_TICKETS ? (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.COMPLETED} (${serviceTicketCount.completedCount.length})`}
                value="completed"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.SPARES_REQUIRED} (${serviceTicketCount.sparesRequired.length})`}
                value="sparesRequired"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.SPARES_RECOMMENDED} (${serviceTicketCount.sparesRecommended.length})`}
                value="sparesRecommended"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.VENDOR_SUPPORT_REQUIRED} (${serviceTicketCount.vendorSupport.length})`}
                value="vendorSupportRequired"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.NOT_COMPLETED} (${serviceTicketCount.notCompleted.length})`}
                value="notCompleted"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`All (${serviceTicketCount.allActionRequired.length})`}
                value="allAction"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
            </Tabs>
            {renderTableData()}
          </>
        ) : pathname.substring(1) === Links.PENDING_SERVICE_TICKETS ? (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.SPARES_REQUIRED_PENDING} (${serviceTicketCount.sparesRequiredpendng.length})`}
                component={Button}
                value="sparesRequiredPending"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.SPARES_RECOMMENDED_PENDING} (${serviceTicketCount.sparesRecommendedPending.length})`}
                value="sparesRecommendedPending"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.VENDOR_SUPPORT_PENDING} (${serviceTicketCount.vendorSupportPending.length})`}
                value="vendorSupportPending"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`All (${serviceTicketCount.allpendingticket.length})`}
                value="allPending"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
            </Tabs>
            {renderTableData()}
          </>
        ) : pathname.substring(1) === Links.ASSIGN_TECHNICIAN_SERVICE_TICKETS ? (
          <>
            <Tabs value={activeTab} onChange={handleTabChange} centered>
              <Tab
                label={`${appConstants?.tabConstants?.safetyTicket || ""} (${serviceTicketCount.safetyTicket?.length || 0})`}
                component={Button}
                value="safetyTicket"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_STATUS?.ASSIGN_SERVICE_ENGINEER} (${serviceTicketCount.assignTech?.length})`}
                component={Button}
                value="assignTech"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_PRIORITY?.HIGH} (${serviceTicketCount.high.length})`}
                value="high"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_PRIORITY?.MEDIUM} (${serviceTicketCount.medium.length})`}
                value="medium"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.allTypes?.SERVICE_TICKET_PRIORITY?.LOW} (${serviceTicketCount.low.length})`}
                value="low"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
              <Tab
                label={`${appConstants?.tabConstants?.vendorTickets || ""} (${serviceTicketCount.vendorTicket.length})`}
                value="vendorTicket"
                sx={{
                  fontWeight: "bold",
                  marginRight: "20px",
                  "&:hover": {
                    backgroundColor: "initial",
                    color: "initial",
                  },
                }}
              />
            </Tabs>
            {renderTableData()}
          </>
        ) : (
          <Box style={{ height: 700, width: "100%", paddingTop: 3, marginTop: 8, overflow: "auto" }}>
            {loading || isLoading ? (
              <TableSkeleton rows={8} columns={columnAttributes.length} />
            ) : (
              <DataGridTable loading={loading} rowId={"ticketId"} data={filteredServiceTickets} columnAttributes={columnAttributes} />
            )}
          </Box>
        )}

        <Dialog open={showExportAlertDialog} onClose={() => setShowExportAlertDialog(false)}>
          <DialogContent>
            <Typography mt={1}>Click "Export filtered" to export only the filtered tickets.</Typography>
            <Typography mt={1}>Click "Export all" to export all tickets.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowExportAlertDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e)}>
              Export filtered
            </Button>
            <Button variant="contained" onClick={(e) => downloadCsv(e, true)}>
              Export all
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};
export default AllServiceTickets;
